/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import { alpha } from "@mui/material/styles";
import { Box, Typography, styled, Tab } from "@mui/material";
import TabList from "@mui/lab/TabList";

export const StyledGuestDetailsPage = styled(Box)({
	height: '100%',
	width: '100%',
	overflowY: 'auto',
	overflowX: 'hidden',
	backgroundColor: '#ffffff',
});

export const StyledGuestDashboardLayout = styled(Box)({
	display: 'grid',
	gridTemplateColumns: '280px minmax(0, 1fr) 280px',
	gap: '16px',
	alignItems: 'start',
	'@media (max-width: 1199px)': {
		gridTemplateColumns: '280px minmax(0, 1fr)',
	},
	'@media (max-width: 899px)': {
		gridTemplateColumns: '1fr',
	},
});

export const StyledSection = styled(Box)({
	display: 'grid',
	gap: '16px',
	minWidth: 0,
});

export const StyledGuestCenterColumn = styled(Box)({
	minWidth: 0,
});

export const StyledGuestPersonalCard = styled(Box)({
	border: '1px solid #d7dde8',
	borderRadius: '12px',
	backgroundColor: '#ffffff',
	padding: '20px',
	boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
});

export const StyledGuestPersonalHeader = styled(Box)({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	paddingBottom: '18px',
	borderBottom: '1px solid #dbe5f1',
	marginBottom: '16px',
});

export const StyledGuestProfileImage = styled('img')({
	width: '88px',
	height: '88px',
	borderRadius: '50%',
	objectFit: 'cover',
	display: 'block',
	marginBottom: '16px',
	backgroundColor: '#eef4fb',
});

export const StyledGuestProfileFallback = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'badgeColor',
})(({ badgeColor }) => ({
	width: '88px',
	height: '88px',
	borderRadius: '50%',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	backgroundColor: alpha(badgeColor, 0.14),
	color: badgeColor,
	fontSize: '28px',
	fontWeight: 700,
	letterSpacing: '0.02em',
	marginBottom: '16px',
}));

export const StyledGuestName = styled(Typography)({
	fontSize: '16px',
	lineHeight: 1.25,
	fontWeight: 700,
	color: '#233044',
	textAlign: 'center',
	marginBottom: '4px',
});

export const StyledGuestSince = styled(Typography)({
	fontSize: '12px',
	lineHeight: 1.3,
	fontWeight: 500,
	color: '#62748a',
	textAlign: 'center',
});

export const StyledGuestContactSection = styled(Box)({
	display: 'grid',
	gap: '14px',
});

export const StyledGuestSectionTitle = styled(Typography)({
	fontSize: '12px',
	lineHeight: 1.2,
	fontWeight: 700,
	letterSpacing: '0.08em',
	textTransform: 'uppercase',
	color: '#61748a',
});

export const StyledGuestContactRow = styled(Box)({
	display: 'grid',
	gridTemplateColumns: '16px minmax(0, 1fr)',
	gap: '10px',
	alignItems: 'start',
});

export const StyledGuestContactContent = styled(Box)({
	minWidth: 0,
});

export const StyledGuestContactLabel = styled(Typography)({
	fontSize: '12px',
	lineHeight: 1.2,
	fontWeight: 500,
	color: '#5d7188',
	marginBottom: '4px',
});

export const StyledGuestContactValue = styled(Typography)({
	fontSize: '14px',
	lineHeight: 1.35,
	fontWeight: 400,
	color: '#233044',
	wordBreak: 'break-word',
});

export const StyledGuestSourceOfTruthSection = styled(Box)({
	display: 'grid',
	gap: '12px',
	marginTop: '16px',
	paddingTop: '16px',
	borderTop: '1px solid #dbe5f1',
});

export const StyledGuestSourceOfTruthChips = styled(Box)({
	display: 'flex',
	flexWrap: 'wrap',
	gap: '8px',
});

const getSourceOfTruthColor = (source) => {
	switch (source) {
		case 'toast':
			return '#EA580C';
		case 'seven_shifts':
			return '#1976d2';
		case 'manual':
			return '#8bc34a';
		default:
			return '#9e9e9e';
	}
};

export const StyledSourceOfTruthChip = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'source',
})(({ source }) => ({
	height: '24px',
	padding: '0 10px',
	borderRadius: '6px',
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	fontSize: '12px',
	lineHeight: 1.2,
	fontWeight: 600,
	whiteSpace: 'nowrap',
	backgroundColor: `${getSourceOfTruthColor(source)}20`,
	color: getSourceOfTruthColor(source),
}));

export const StyledGuestPersonalSkeletonWrap = styled(Box)({
	width: '100%',
	display: 'grid',
	justifyItems: 'center',
	gap: '10px',
});

export const CircularLoaderContainer = styled('div')({
  width: '88px',
  height: '88px',
  borderRadius: '50%',
})

export const StyledTab = styled(Tab)({
  textTransform: 'none',
  fontSize: '15px',
})

export const StyledGuestTabList = styled(TabList)({
	'& .MuiTabs-flexContainer': {
		flexWrap: 'nowrap',
	},
	'& .MuiTab-root': {
		flexShrink: 0,
	},
})
